import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import uploaderShot1x from "@/assets/web-uploader/uploader-desktop-1x.webp";
import uploaderShot2x from "@/assets/web-uploader/uploader-desktop-2x.webp";
import { BracketMark } from "./PhotoDemo";
import { APP_PATH, APP_WAITLIST, FIND_PATH, LOGIN_PATH, PERSONAL_APP_STORE_URL, REGISTER_PATH, STUDIO_APP, STUDIO_PATH } from "./paths";
import { PRODUCT_NAME, ProductBadge, ProductLockup, SearchIcon, type Product } from "./products";
import { BTN_OUTLINE_MD, BTN_PRIMARY_MD, CARD, FOCUS, TEXT_LINK } from "./ui";
import { PhoneFrame, useAppShot } from "./appScreens";

// The dashboard's buttons at their default size (44 px), as in the admin's cards.
const PRIMARY = `${BTN_PRIMARY_MD} text-[15px]`;
const OUTLINE = `${BTN_OUTLINE_MD} text-[15px]`;

/** The App Store's mark (Apple logo), for the store button. */
const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.4 12.6c0-2.4 2-3.5 2-3.6-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9-.7 0-1.8-.8-3-.8-1.5 0-3 .9-3.8 2.3-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.4 2.9 2.3 1.2 0 1.6-.7 3-.7s1.8.7 3 .7c1.3 0 2.1-1.1 2.8-2.3.9-1.3 1.3-2.6 1.3-2.6s-2.5-1-2.5-3.7zM14.2 5.6c.6-.8 1.1-1.8 1-2.9-.9 0-2.1.6-2.7 1.4-.6.7-1.1 1.8-1 2.8 1 .1 2.1-.5 2.7-1.3z" />
  </svg>
);

/** The Personal app in the App Store: a store button once the listing is live (PERSONAL_APP_STORE_URL), and until
 *  then a plain "Coming soon to the App Store" label (not a link: there's nothing to get yet). */
export const AppStoreButton = () => {
  const { t } = useTranslation();
  if (PERSONAL_APP_STORE_URL) {
    return (
      <a href={PERSONAL_APP_STORE_URL} className={OUTLINE} data-testid="app-store-link">
        <AppleIcon />
        {t("home.doors.personal.appStore")}
      </a>
    );
  }
  return (
    <span className="inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-xl border-2 border-dashed border-input px-4 text-[15px] font-semibold text-muted-foreground" data-testid="app-store-soon">
      <AppleIcon />
      {t("home.doors.personal.appStoreSoon")}
    </span>
  );
};

const Point = ({ children }: { children: string }) => (
  <li className="flex items-start gap-2.5">
    <span className="mt-1">
      <BracketMark size={14} />
    </span>
    {children}
  </li>
);

/**
 * The two doors, side by side: Photohawk Personal (the phone app for your own photos: the App Store, a waitlist
 * until the listing is live, never a web sign-up) and Photohawk Studio (for photographers and events: start free or
 * sign in on the web, where Studio works today; the Studio app comes in November 2026, so its button goes to the
 * app's showcase on /studio, never to a download). Each says who it's for, its one-line promise, shows the product itself
 * (the app's People screen; the Studio uploader) and its own next step. White cards, as the dashboard's, on the
 * page's white or the muted band.
 */
export const PathCards = ({ idPrefix }: { idPrefix: string }) => {
  const { t } = useTranslation();
  const d = (key: string) => t(`home.doors.${key}`);
  const peopleShot = useAppShot("people");
  const shareShot = useAppShot("share");
  const card = `${CARD} flex min-w-0 flex-col overflow-hidden`;
  // The product itself, under the door's name and promise: a short window onto the app or the uploader, on the
  // dashboard's muted grey.
  const stage = "relative h-[clamp(150px,14vw,190px)] overflow-hidden rounded-xl border border-border bg-muted";

  const head = (product: Product) => (
    <div className="flex flex-col gap-3">
      <span className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <ProductLockup product={product} size="lg" />
        <ProductBadge product={product} />
      </span>
      <span className="text-[13px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{d(`${product}.for`)}</span>
      <h3 id={`${idPrefix}-${product}`} className="m-0 font-display text-[clamp(22px,2.2vw,28px)] font-bold leading-tight tracking-[-0.02em] text-foreground sora:text-[clamp(21px,2vw,26px)]">
        {d(`${product}.promise`)}
      </h3>
    </div>
  );

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(min(340px,100%),1fr))] gap-5" data-testid={`${idPrefix}-paths`}>
      <article className={card} aria-labelledby={`${idPrefix}-personal`} data-testid="path-app" data-product="personal">
        <div className="flex flex-1 flex-col gap-4 p-[clamp(22px,3vw,32px)]">
          {head("personal")}
          {/* The app itself: the People and Share screens, in two phones rising from the bottom of the stage. */}
          <div className={stage} data-testid="path-app-phone">
            <div className="absolute left-[16%] top-4 w-[min(28%,140px)] -rotate-[5deg]">
              <PhoneFrame shot={peopleShot} alt={t("home.personal.screens.alt.people")} sizes="140px" />
            </div>
            <div className="absolute right-[16%] top-8 w-[min(28%,140px)] rotate-[4deg]">
              <PhoneFrame shot={shareShot} alt={t("home.personal.screens.alt.share")} sizes="140px" />
            </div>
          </div>
          <ul className="m-0 flex list-none flex-col gap-2.5 p-0 text-base leading-snug text-muted-foreground">
            <Point>{d("personal.point1")}</Point>
            <Point>{d("personal.point2")}</Point>
          </ul>
          <p className="m-0 text-sm font-semibold text-foreground" data-testid="personal-platforms">
            {d("personal.platforms")}
          </p>
          <div className="mt-auto flex flex-wrap gap-3 pt-1">
            <AppStoreButton />
            {!PERSONAL_APP_STORE_URL && (
              <Link to={APP_WAITLIST} className={PRIMARY}>
                {t("home.joinWaitlist")}
              </Link>
            )}
          </div>
          <p className="m-0 flex flex-wrap items-center gap-x-3 text-sm text-muted-foreground">
            <span>{d("personal.signIn")}</span>
            <Link to={APP_PATH} className={TEXT_LINK}>
              {d("personal.learnMore")} <span aria-hidden="true">→</span>
            </Link>
          </p>
        </div>
      </article>

      <article className={card} aria-labelledby={`${idPrefix}-studio`} data-testid="path-events" data-product="studio">
        <div className="flex flex-1 flex-col gap-4 p-[clamp(22px,3vw,32px)]">
          {head("studio")}
          {/* Studio itself: the uploader, a race gallery filling up as the photos upload. */}
          <div className={stage}>
            <img
              src={uploaderShot1x}
              srcSet={`${uploaderShot1x} 1200w, ${uploaderShot2x} 2400w`}
              sizes="(min-width: 1024px) 640px, 100vw"
              width={1200}
              height={750}
              alt={d("studio.shotAlt")}
              loading="lazy"
              decoding="async"
              className="absolute left-[5%] top-4 block w-[150%] max-w-none rounded-tl-lg border border-border shadow-lg sm:w-[120%]"
              data-testid="path-studio-shot"
            />
          </div>
          <ul className="m-0 flex list-none flex-col gap-2.5 p-0 text-base leading-snug text-muted-foreground">
            <Point>{d("studio.point1")}</Point>
            <Point>{d("studio.point2")}</Point>
            <Point>{d("studio.point3")}</Point>
          </ul>
          <p className="m-0 text-sm font-semibold text-foreground" data-testid="studio-platforms">
            {d("studio.platforms")}
          </p>
          <div className="mt-auto flex flex-wrap gap-3 pt-1">
            <Link to={REGISTER_PATH} className={PRIMARY}>
              {t("home.startFree")}
              <span className="sr-only">: {PRODUCT_NAME.studio}</span>
            </Link>
            <Link to={STUDIO_APP} className={OUTLINE} data-testid="path-studio-app">
              {d("studio.appSoon")}
            </Link>
          </div>
          <p className="m-0 flex flex-wrap items-center gap-x-3 text-sm text-muted-foreground">
            <span>
              {d("studio.signInLead")}{" "}
              <Link to={LOGIN_PATH} className={`rounded-md font-semibold text-foreground underline underline-offset-4 ${FOCUS}`}>
                {d("studio.signIn")}
              </Link>
            </span>
            <Link to={STUDIO_PATH} className={TEXT_LINK}>
              {d("studio.learnMore")} <span aria-hidden="true">→</span>
            </Link>
          </p>
        </div>
      </article>
    </div>
  );
};

/** The participants' line: "Looking for your event photos? Find them →" (/find-your-photos). */
export const FindLine = () => {
  const { t } = useTranslation();
  return (
    <p className="m-0 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl border border-border bg-muted px-5 py-4 text-base text-foreground" data-testid="find-line">
      <span className="flex items-center gap-2.5 font-semibold">
        <span className="text-accent-foreground">
          <SearchIcon size={20} />
        </span>
        {t("home.story.find.question")}
      </span>
      <Link to={FIND_PATH} className={`${TEXT_LINK} font-bold`}>
        {t("home.story.find.link")} <span aria-hidden="true">→</span>
      </Link>
    </p>
  );
};
